import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { authApi, type LoginParams, type RegisterParams } from '@/services/api';

interface User {
  id: string;
  accountType: 'personal' | 'company';
  companyName?: string;
  creditCode?: string;
  contactName: string;
  phone: string;
  email: string;
  region?: string;
  role?: 'supplier' | 'buyer';
  avatar?: string;
  token?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (phone: string, password: string, captchaId: string, captchaCode: string) => Promise<{ success: boolean; error?: string }>;
  register: (data: RegisterParams) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

const CURRENT_USER_KEY = 'greencert_current_user';

function getStoredCurrentUser(): User | null {
  try {
    const stored = localStorage.getItem(CURRENT_USER_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(getStoredCurrentUser);
  const [isLoading, setIsLoading] = useState(false);

  const login = useCallback(async (phone: string, password: string, captchaId: string, captchaCode: string) => {
    setIsLoading(true);
    try {
      const params: LoginParams = { phone, password, captchaId, captchaCode };
      const { user: u, token } = await authApi.login(params);
      const userData = { ...u, token } as User;
      localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(userData));
      setUser(userData);
      return { success: true };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : '手机号或密码错误，请重试' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (data: RegisterParams) => {
    setIsLoading(true);
    try {
      const { user: u, token } = await authApi.register(data);
      const userData = { ...u, token } as User;
      localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(userData));
      setUser(userData);
      return { success: true };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : '注册失败，请稍后重试' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(CURRENT_USER_KEY);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        register,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}